import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { findMixLineupEntry } from "@/config/mixLineup";
import {
  createDockingMediator,
  type DockingEffect,
  type DockingEnvironment,
  type DockingEvent,
  type DockingState,
  isDockingEvent,
} from "@/features/mix-onboarding/dockingMediator";
import {
  isRunningAsInstalledPwa,
  type PwaInstallPromptState,
  type PwaInstallPromptStore,
  pwaInstallPromptStore,
} from "@/features/mix-onboarding/lib/pwaInstallPrompt";
import {
  resolveEnabledMixSlugs,
  useMixStore,
} from "@/features/settings/stores/mixStore";
import type { EventNode, TypedEvent } from "@/shared/lib/componentHierarchy";
import {
  useComponentNode,
  useHierarchyLifetime,
  useMediatorSnapshot,
} from "@/shared/ui/component-hierarchy/hooks";

export interface DockingOnboardingWallProps {
  registeredSlugs: readonly string[];
  onComplete: (isAdult: boolean) => void;
  installPromptStore?: PwaInstallPromptStore;
  isInstalledPwa?: () => boolean;
  isMobileDisplay: boolean;
  requiresAdultConfirmation?: boolean;
}
export interface DockingPresentation extends DockingState {
  node: EventNode<TypedEvent>;
  installPromptState: PwaInstallPromptState;
  onEvent: (event: DockingEvent) => void;
}

export function useDockingPresenter({
  registeredSlugs,
  onComplete,
  installPromptStore = pwaInstallPromptStore,
  isInstalledPwa = isRunningAsInstalledPwa,
  isMobileDisplay,
  requiresAdultConfirmation = false,
}: DockingOnboardingWallProps): DockingPresentation {
  const boardSettings = useMixStore((state) => state.boardSettings);
  const installPromptState = useSyncExternalStore(
    installPromptStore.subscribe,
    installPromptStore.getState,
    installPromptStore.getState,
  );
  const environment = (): DockingEnvironment => ({
    includesAdultBoard: resolveEnabledMixSlugs(
      registeredSlugs,
      boardSettings,
    ).some((slug) => findMixLineupEntry(slug)?.isAdult === true),
    requiresAdultConfirmation,
    skipInstall:
      !isMobileDisplay ||
      isInstalledPwa() ||
      installPromptStore.getState() === "installed",
  });
  const current = { environment, onComplete, installPromptStore };
  const latest = useRef(current);
  useLayoutEffect(() => {
    latest.current = current;
  });
  const [mediator] = useState<ReturnType<typeof createDockingMediator>>(() =>
    createDockingMediator(
      registeredSlugs.some(
        (slug) => findMixLineupEntry(slug)?.isAdult === false,
      ),
      (effect: DockingEffect): void => {
        if (effect.type === "complete") {
          latest.current.onComplete(effect.isAdult);
          return;
        }
        void (async (): Promise<void> => {
          try {
            const outcome =
              await latest.current.installPromptStore.requestInstall();
            mediator.dispatch({
              type: "docking.install-result",
              succeeded: outcome !== "unavailable",
              environment: latest.current.environment(),
            });
          } catch {
            mediator.dispatch({
              type: "docking.install-result",
              succeeded: false,
              environment: latest.current.environment(),
            });
          }
        })();
      },
    ),
  );
  useHierarchyLifetime(mediator);
  const state = useMediatorSnapshot(mediator);
  const node = useComponentNode((event) => {
    if (!isDockingEvent(event)) return "unhandled";
    return mediator.dispatch({
      ...event,
      environment: latest.current.environment(),
    }).status === "handled"
      ? "handled"
      : "unhandled";
  });
  useEffect(() => {
    if (installPromptState === "installed")
      node.dispatch({ type: "docking.installed" });
  }, [node, installPromptState]);
  useEffect(() => {
    if (state.step !== "install") return;
    const handleEscape = (event: KeyboardEvent): void => {
      if (event.key !== "Escape" || event.isComposing) return;
      if (node.dispatch({ type: "docking.skip" }).status === "handled")
        event.preventDefault();
    };
    document.addEventListener("keydown", handleEscape);
    return (): void => document.removeEventListener("keydown", handleEscape);
  }, [node, state.step]);
  return { ...state, node, installPromptState, onEvent: node.dispatch };
}
